import React, { useState, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { BaseView, BaseText } from '@/components/base'
import { PhoneNumberInput } from '@/components/PhoneNumberInput'
import { AppButton } from '@/components/AppButton'
import { StickyHeaderNavigation } from '@/components'
import { useUserStore } from '@/store'
import { useAddPhoneStore } from '@/store/useAddPhoneStore'
import { PhoneNumberValue } from '@/components/PhoneNumberInput/types'
import { inputLimits } from '@/constants/inputLimits'
import { useSendPhoneVerificationCode } from '@/domain/identity'

interface PhoneState {
  phoneNumber: string
  countryCode: string
  countryIsoCode: string
  isValid: boolean
  errorMessage: string
}

export const EnterPhoneScreen: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const userAuthenticated = useUserStore((state) => state.userAuthenticated)

  // Add phone store
  const setPhone = useAddPhoneStore((state) => state.setPhone)
  const setMessagingMethod = useAddPhoneStore(
    (state) => state.setMessagingMethod,
  )

  const [state, setState] = useState<PhoneState>({
    phoneNumber: '',
    countryCode: '+965',
    countryIsoCode: 'KW',
    isValid: false,
    errorMessage: '',
  })

  const handlePhoneChange = useCallback((phone: PhoneNumberValue) => {
    // Simple validation based on length
    const phoneDigits = phone.number.replace(/\D/g, '')
    const isValidLength =
      phoneDigits.length >= inputLimits.phone.min &&
      phoneDigits.length <= inputLimits.phone.max

    setState((prev) => ({
      ...prev,
      phoneNumber: phone.number,
      countryCode: phone.countryCode,
      countryIsoCode: phone.countryIsoCode,
      errorMessage:
        !isValidLength && phoneDigits.length > 0
          ? `Phone number must be between ${inputLimits.phone.min} and ${inputLimits.phone.max} digits`
          : '',
      isValid: isValidLength,
    }))
  }, [])

  // API Hook
  const { mutateAsync: sendVerificationCode, isPending: isSendingCode } =
    useSendPhoneVerificationCode({
      onSuccess: () => {
        // Store phone data for verification screen
        setPhone({
          countryCode: state.countryCode,
          countryIsoCode: state.countryIsoCode,
          number: state.phoneNumber,
        })

        // Set default messaging method
        setMessagingMethod({
          code: 'sms',
        })

        // Navigate to verification screen
        navigate('/add-phone/verification')
      },
      onError: ({ errorMessage }) => {
        setState((prev) => ({
          ...prev,
          errorMessage:
            errorMessage ||
            'Failed to send verification code. Please try again.',
        }))
      },
    })

  const handleContinue = useCallback(async () => {
    if (!state.isValid || isSendingCode) return

    setState((prev) => ({ ...prev, errorMessage: '' }))

    const fullPhoneNumber = `${state.countryCode}${state.phoneNumber.replace(/\D/g, '')}`

    sendVerificationCode({
      phone: fullPhoneNumber,
      send_method: 'sms', // Default to SMS for now
    })
  }, [
    state.isValid,
    state.countryCode,
    state.phoneNumber,
    isSendingCode,
    sendVerificationCode,
  ])

  // Redirect if user already has phone number
  useEffect(() => {
    if (userAuthenticated?.phone_number) {
      navigate('/dashboard')
    }
  }, [userAuthenticated?.phone_number, navigate])

  return (
    <BaseView flex={1} gap={20} px={12}>
      {/* Header Section */}
      <BaseView gap={28}>
        <StickyHeaderNavigation />
        <BaseText fontSize={32} className="font-semibold text-left">
          {t('screens:addPhone.title')}
        </BaseText>
      </BaseView>

      {/* Content Section with full height flex */}
      <div className="h-full justify-between flex flex-col">
        <BaseView gap={8}>
          {/* Description */}
          <BaseView mb={20}>
            <BaseText
              fontSize={16}
              fontFamily="regular"
              color="label.description"
            >
              {t('screens:addPhone.enterPhone.description')}
            </BaseText>
          </BaseView>

          {/* Phone Input */}
          <PhoneNumberInput
            value={{
              number: state.phoneNumber,
              countryCode: state.countryCode,
              countryIsoCode: state.countryIsoCode,
            }}
            onChange={handlePhoneChange}
            errorMessage={state.errorMessage}
            withoutBorder={true}
          />
        </BaseView>

        {/* Button Section */}
        <BaseView className="mb-3">
          <AppButton
            label={t('form:actions.continue')}
            onPress={handleContinue}
            disabled={!state.isValid}
            isLoading={isSendingCode}
          />
        </BaseView>
      </div>
    </BaseView>
  )
}

export default EnterPhoneScreen
